import { StorageHandler, createInMemoryStorageHandler } from "@/lib/storage";
import { getWindow } from "@/lib/window";
import { Checkpoint } from "../tourPlayback";
import { uuid } from "@/lib/utils";
import { findLastCheckpoint } from "../checkpoint";

export interface TourState {
  activeTourGuideId?: string;
  activeStepId?: string;
  lastStepTransitionTimestamp?: number;
  checkpoints?: Checkpoint[];
  tabId?: string;
  tourPlaybackVersion?: string;
  lastCompletedStepId?: string;
  hasWatchedIntro?: boolean;
  lastPageUrl?: string;
  isSuspended?: boolean;
  suspendReason?: string;
  expirationTimeoutMinutes?: number;
  startedAt?: number;
  sessionId?: string;
  initialUrl?: string;
}

export type TourStateStorage = ReturnType<typeof buildTourStateStorage>;

const storageKey = (suffix: boolean) => `pyne-tour-state${suffix ? `-preview` : ""}`;
export const buildTourStateStorage = (suffix: boolean, storage: StorageHandler) => {
  const self = () => buildTourStateStorage(suffix, storage);
  return {
    key: () => {
      return storageKey(suffix);
    },
    get: (): TourState => {
      return storage.get(storageKey(suffix), { checkpoints: [] });
    },
    clear: () => {
      storage.remove(storageKey(suffix));
    },
    set: (state: Partial<TourState>) => {
      storage.set(storageKey(suffix), { ...self().get(), ...state });
    },
    setCheckpoints: (checkpoints: Checkpoint[]) => {
      self().set({ checkpoints });
    },
    listCheckpoints: (): Checkpoint[] => {
      return self().get().checkpoints || [];
    },
    getLastCheckpoint: (): Checkpoint | null => {
      const checkpoints = self().listCheckpoints();
      if (checkpoints.length === 0) {
        return null;
      }

      return findLastCheckpoint(checkpoints);
    },
    setLastCheckpointUrl: (stepId: string, url: string) => {
      const checkpoints = self().listCheckpoints();
      const checkpointIndex = checkpoints.findIndex((checkpoint) => checkpoint.stepId === stepId);
      if (checkpointIndex === -1) {
        return;
      }

      checkpoints[checkpointIndex].url = url;
      self().set({ checkpoints });
    },
    removeCheckpointByStepId: (stepId: string) => {
      const checkpoints = self().listCheckpoints();
      const checkpointIndex = checkpoints.findIndex((checkpoint) => checkpoint.stepId === stepId);
      if (checkpointIndex === -1) {
        return;
      }

      checkpoints.splice(checkpointIndex, 1);
      self().set({ checkpoints });
    },
    setLastCompletedStepId: (stepId: string) => {
      self().set({ lastCompletedStepId: stepId });
    },
    getLastCompletedStepId: () => {
      return self().get().lastCompletedStepId;
    },
    onTabChange: (callback: (tabId: string) => void) => {
      getWindow().addEventListener("storage", (event) => {
        if (event.key === storageKey(suffix)) {
          const oldState = JSON.parse(event.oldValue || "{}") as TourState;
          const newState = JSON.parse(event.newValue || "{}") as TourState;

          if (oldState.tabId !== newState.tabId) {
            callback(newState.tabId!);
          }
        }
      });
    },
    getTabId: () => {
      return self().get().tabId;
    },
    setTabId: (tabId: string) => {
      self().set({ tabId });
    },
    setHasWatchedIntro: (value: boolean) => {
      self().set({ hasWatchedIntro: value });
    },
    getHasWatchedIntro: () => {
      return self().get().hasWatchedIntro;
    },
    getIsSuspended: () => {
      return self().get().isSuspended;
    },
    setIsSuspended: (isSuspended: boolean) => {
      self().set({ isSuspended });
    },
    initStartedAt: () => {
      const state = self().get();
      if (state.startedAt) {
        return state.startedAt;
      }

      const time = Date.now();
      self().set({ startedAt: time });
      return time;
    },
    initSessionId: () => {
      const state = self().get();
      if (state.sessionId) {
        return state.sessionId;
      }

      const sessionId = uuid();
      self().set({ sessionId });
      return sessionId;
    },

    getInitialUrl: () => {
      return self().get().initialUrl;
    },
  };
};

export const buildInMemoryTourStateStorage = () => buildTourStateStorage(false, createInMemoryStorageHandler());
